import { v4 } from "uuid";
import { fetchJson } from "../../net/fetch";
import { SystemClassListToFlags } from "../schema/system-class";
import { System, Subsystem, SystemElement, SystemConnectionType } from "./Connectivity";
import * as dte from "../DtEventTypes";
import { DtConstants } from "../schema/DtConstants";
import { DtFacility } from "../DtFacility";
import { ConnectivityUtils as CU } from "./ConnectivityUtils";
import { RowKeySize, ElementFlags, QC, ElementFlagsSize, ElementIdSize } from "../schema/dt-schema";
import { base64EncArr, base64DecToArr } from "../encoding/base64";
import { vlqDecode, vlqEncode } from "../encoding/varint";
import { SystemVisualizer } from "./SystemVisualizer";
import { isNodeJS } from "../../compat";
import { BigEndian } from "../encoding/big-endian";
import { FacetTypes } from "../facets/Facets";

// base64 encoded "[0]" byte array
const STANDALONE_ELEMENT_CONN_VAL = "AA";

const SYSTEM_CATEGORY = DtConstants.RC['MEP System'];

function encodeSystemFilters(stringsFilter) {
  const res = {};

  if (stringsFilter.systemClasses?.length) {
    res.systemClasses = SystemClassListToFlags(stringsFilter.systemClasses);
  }

  // TODO: Add filter checks here when we allow more system filters

  return res;
}

let keyBuff = new Uint8Array(24);

// Used for logical System elements stored in default model
function vlqID2ElementKey(encodedID, skipFlags) {
  const keyLen = base64DecToArr(DtConstants.SystemElementFirstKey, keyBuff);
  const decodedID = vlqDecode(encodedID);

  BigEndian.encodeInt32(keyBuff, keyLen - 4, decodedID);

  return skipFlags ?
  base64EncArr(keyBuff, ElementFlagsSize, ElementIdSize) :
  base64EncArr(keyBuff);
}

// Used for logical System elements stored in default model
function elementKey2vlqID(elementKey) {
  const keyLen = base64DecToArr(elementKey, keyBuff);

  let id = BigEndian.decodeInt32(keyBuff, keyLen - 4);

  return vlqEncode(id);
}


/**
 * Merge multiple System filters into one to speed up element isolation.
 * @param {object[]} filters List of System.filter
 * @returns {object} A combined System.filter
 */
function mergeSystemFilters(filters) {

  const res = {
    systemClasses: 0
  };

  for (const filter of filters) {
    if (filter.systemClasses) {
      res.systemClasses |= filter.systemClasses;
    }

    // TODO: Add filter merge here when we allow more system filters
  }

  return res;
}

/**
 * Manages the lifecycle of systems at the facility level through API endpoints.
 */
export class SystemsManager {

  /**
   * @constructor
   * @param {DtFacility} facility
   */
  constructor(facility) {
    this.facility = facility;
    this.systemsCache;

    if (!isNodeJS()) {
      this.visualizer = new SystemVisualizer();
    }

    this.boundOnModelChanged = this.onModelChangedEventHandler.bind(this);
  }

  init() {
    this.facility.eventTarget.addEventListener(dte.DT_MODEL_CHANGED_EVENT, this.boundOnModelChanged);
    this.visualizer?.init(this.facility);
  }

  dispose() {
    this.facility.eventTarget.removeEventListener(dte.DT_MODEL_CHANGED_EVENT, this.boundOnModelChanged);
    this.visualizer?.dispose();
    delete this.systemsCache;
  }

  /**
   * Handles model events and dispatch notification if systems changed
   * @param {(string | object)} event
   */
  async onModelChangedEventHandler(event) {
    let systemsChanged = false;

    switch (event.change.ctype) {
      case DtConstants.ChangeTypes.Mutate:
        if (event.change.isOwn) {
          // Process non-defaultModel mutations from the current session
          systemsChanged = await this.onOwnMutateEvent(event);
        } else if (this.systemsCache) {
          systemsChanged = await this.onRemoteMutateEvent(event);
        }
        break;
      case DtConstants.ChangeTypes.UpdateSystemConnections:
        if (this.systemsCache) {
          systemsChanged = await this.onRemoteConnectionsUpdateEvent(event);
        }
        break;
    }

    // Notify of system changes
    if (systemsChanged) {
      this.facility.eventTarget.dispatchEvent({
        type: dte.DT_SYSTEMS_CHANGED_EVENT,
        change: { ctype: DtConstants.ChangeTypes.UpdateSystems }
      });
    }
  }

  /**
   * When a mutation from the current session happens,
   * we need to check whether a system filtered property is affected and mark the affected system(s) as dirty.
   * @param {*} event
   * @returns {boolean} systemsChanged
   */
  async onOwnMutateEvent(event) {
    const { details: { systemClassesDiff, attributeHashes }, dbIdsRemoved, facetInfo, modelId } = event.change;
    const isDefaultModelEvent = modelId === this.facility.getDefaultModelId();
    const affectedSystemClasses = systemClassesDiff ?? dbIdsRemoved?.reduce((acc, dbId) => acc |= facetInfo.dbId2SystemClass[dbId], 0);

    if (isDefaultModelEvent) {
      if (this.systemsCache && this.isSystemEvent(event)) {
        // System properties changed, need to refetch all systems
        await this.getAll(true);
        return true;
      }

      return false;
    } else if (attributeHashes && this.systemsCache?.values().some((s) => attributeHashes.has(s.subsystemAttrHash))) {
      // Non-default model mutation affects subsystem attributes
      await this.getAll(true);
      return true;
    } else if (!affectedSystemClasses) {
      // No-op if non system mutation doesn't affect Filtered properties
      return false;
    }

    // Check if any system is affected by the mutation and mark them as dirty
    const systems = await this.getAll();
    const affectedSysIds = systems.filter((s) => s.filter.systemClasses & affectedSystemClasses).map((s) => s.id);
    const changedSystems = await this.setAsDirty(affectedSysIds);

    if (!changedSystems) {
      return false;
    }

    for (const system of changedSystems) {
      if (system.hasCachedConnections()) {
        // Connections are not really updated, we just need to update the
        // connectivity graph so new elements behave like they are part of the system.
        // countSystemElements will be called later
        await this.getConnections(system.id, true);
      } else {
        CU.countSystemElements(this.facility, system);
      }
    }

    return true;
  }

  /**
   * Update cached systems when a remote DT_SYSTEM_CONNECTIONS_CHANGED_EVENT is received
   * @param {*} event
   * @returns {boolean} systemsChanged
   */
  async onRemoteConnectionsUpdateEvent(event) {
    let systemsChanged = false;

    // Backend mapping was performed on a system (there should only be one id)
    for (const sysId of event.change.details.ids) {
      systemsChanged = true;
      const system = await this.get(sysId);
      if (system?.hasCachedConnections()) {
        // Only update cached systems
        // TODO: debounce this, otherwise one reload per model with changes.
        system.isDirty = false; // We know this has to be true since a mapping was done on this system
        await this.getConnections(sysId, true);

        this.facility.eventTarget.dispatchEvent({ type: dte.DT_SYSTEM_CONNECTIONS_CHANGED_EVENT, systemId: sysId });
      } else if (system) {
        // No cached connections, needs a recount
        system.isDirty = false; // We know this has to be true since a mapping was done on this system
        CU.countSystemElements(this.facility, system);
      }
    }

    return systemsChanged;
  }

  /**
   * Non-default model: check whether it affects system connections, and update connections of affected systems.
   * Default model: Update the system properties of affected systems
   * @param {*} event
   * @returns {boolean} systemsChanged
   */
  async onRemoteMutateEvent(event) {
    const isDefaultModelEvent = event.change.modelId === this.facility.getDefaultModelId();
    let systemsChanged = false;

    if (!isDefaultModelEvent) {
      /** Connections Edit check */

      // Check if it's system related
      const attrs = event.change.details.attributes;
      const attributeHashes = event.change.details.attributeHashes;
      const { dbIdsRemoved, facetInfo } = event.change;
      const affectedSystemClasses = dbIdsRemoved?.reduce((acc, dbId) => acc |= facetInfo.dbId2SystemClass[dbId], 0);

      // Needs dedup if multiple changes to the same system
      let systemsToUpdate = new Set();

      for (const attr of attrs) {
        const [fam, col] = attr.split(":");

        if (fam === DtConstants.ColumnFamilies.Systems) {
          // Should be an override column, but check just in case
          const sysId = col[0] === "!" ? col.slice(1) : col;
          systemsToUpdate.add(sysId);
        }
      }

      if (affectedSystemClasses || attributeHashes) {
        for (const [_, system] of this.systemsCache) {
          if (system.filter.systemClasses & affectedSystemClasses || attributeHashes?.has(system.subsystemAttrHash)) {
            systemsToUpdate.add(system.id);
          }
        }
      }

      systemsChanged = !!systemsToUpdate.size;

      for (const sysId of systemsToUpdate) {
        const system = this.systemsCache.get(sysId);

        if (system && system.hasCachedConnections()) {
          // TODO: only reload affected elements' connections
          // const changes = event.change.addedElements || event.change.changedElements;
          await this.getConnections(sysId, true);

          this.facility.eventTarget.dispatchEvent({ type: dte.DT_SYSTEM_CONNECTIONS_CHANGED_EVENT, systemId: sysId });
        } else if (system) {
          // No cached connections, needs a recount
          CU.countSystemElements(this.facility, system);
        }
      }
    } else if (this.isSystemEvent(event)) {
      await this.getAll(true);
      systemsChanged = true;
    }

    return systemsChanged;
  }

  isSystemEvent(event) {
    /** Systems/Subsystems info changed */
    const dbIdsChanged = event.change.dbIds ?? [];
    const dbIdsRemoved = event.change.dbIdsRemoved ?? [];

    const systems = Array.from(this.systemsCache?.values() ?? []);
    const defaultModel = this.facility.getDefaultModel();
    const dbId2flags = defaultModel.getData().dbId2flags;

    // Check if a system/subsystem's properties changed
    if (dbIdsChanged.some((dbId) => dbId2flags[dbId] === DtConstants.ElementFlags.System)) {
      return true;
    }

    // Check if a system/subsystem was deleted
    return systems.some((sys) => {
      // Check if the system itself was deleted
      if (dbIdsRemoved.includes(sys.dbId)) return true;
      // Check if any subsystem was deleted
      return sys.subsystems?.values().some((subsystem) => dbIdsRemoved.includes(subsystem.dbId));
    });
  }

  /**
   * Create a new system for the current facility
   * @param {String} name
   * @param {*} stringsFilter
   * @param {Number} tolerance
   * @returns {Promise<System>}
   */
  async create(name, stringsFilter, tolerance, mapCenterLines = false) {

    let defaultModel = this.facility.getDefaultModel();
    let newElementKey = DtConstants.SystemElementFirstKey;

    if (!defaultModel) {
      defaultModel = await this.facility.createDefaultModel();
    } else {
      newElementKey = await defaultModel.getNewSystemElementKey();
    }

    const filter = encodeSystemFilters(stringsFilter);
    const config = { tolerance, mapCenterLines };

    const props = {
      [QC.RowKey]: newElementKey,
      [QC.Name]: name,
      [QC.CategoryId]: SYSTEM_CATEGORY,
      [QC.ElementFlags]: ElementFlags.System,
      [QC.UniformatClass]: "D", // Services
      [QC.SystemClass]: filter.systemClasses,
      [QC.Config]: config
    };

    const [dbId] = await defaultModel.createElementsWithData([props], DtConstants.ChangeDesc.CreateSystem, undefined, { returnDbIds: true });
    const id = elementKey2vlqID(newElementKey);
    const newSystem = new System(id, dbId, name, filter, tolerance, true, mapCenterLines);
    this.systemsCache.set(id, newSystem);

    // Trigger mapping
    // Wait for the success response so the UI doesn't block if trigger fails
    await this.map(id);

    return newSystem;
  }

  /**
   * @param {String} id
   * @param {Boolean} forceRefetch
   * @returns {Promise<System>}
   */
  async get(id, forceRefetch = false) {

    if (this.systemsCache?.has(id) && !forceRefetch) {
      return this.systemsCache.get(id);
    }

    // Refresh cached values
    await this.getAll(true);

    return this.systemsCache.get(id);
  }

  /**
   * @param {Boolean} forceRefetch
   * @returns {Promise<Array<System>>}
   */
  async getAll(forceRefetch = false) {

    if (this.systemsCache && !forceRefetch) {
      return [...this.systemsCache.values()];
    }

    const defaultModel = this.facility.getDefaultModel();
    const newCache = new Map();

    if (defaultModel) {
      // Wait for default model and its instance tree
      await defaultModel.waitForLoad(false, true);

      const dmSystems = await defaultModel.getLogicalSystemElements();

      // Populate new cache with fetched systems
      for (const dmSystem of dmSystems) {
        const { dbId, externalId, name, filter, tolerance, isDirty, mapCenterLines } = dmSystem;
        let id = elementKey2vlqID(externalId);
        const fetchedSystem = new System(id, dbId, name, filter, tolerance, isDirty, mapCenterLines);

        const cached = this.systemsCache?.get(id);
        if (cached) {
          // Preserve cached connections, if any
          fetchedSystem.connections = cached.connections;
        }

        if (dmSystem.subsystemAttrHash) {
          fetchedSystem.subsystemAttrHash = dmSystem.subsystemAttrHash;

          for (const data of dmSystem.subsystems) {
            const subsystem = new Subsystem(fetchedSystem, data.dbId, data.attrHash, data.id, data.name);
            fetchedSystem.addSubsystem(subsystem);
          }

          await this.updateSubsystemElements(fetchedSystem);
        }

        newCache.set(id, fetchedSystem);
      }
    }

    this.systemsCache = newCache;
    return [...this.systemsCache.values()];
  }

  /**
   * @param {String} id
   * @param {String} name
   * @param {*} stringsFilter
   * @param {Number} tolerance
   * @returns {Promise<System>}
   */
  async update(id, name, stringsFilter, tolerance, mapCenterLines = false) {

    const defaultModel = this.facility.getDefaultModel();
    if (!defaultModel) {
      return;
    }

    const filter = encodeSystemFilters(stringsFilter);
    const current = await this.get(id);

    let hasChange;
    let isDirty = false;
    const changes = {};
    // Find/store diffs
    if (name !== current.name) {
      hasChange = true;
      changes[QC.Name] = name;
    }

    if (filter.systemClasses !== current.filter.systemClasses) {
      isDirty = true;
      hasChange = true;
      changes[QC.SystemClass] = filter.systemClasses;
    }

    // If one value changes in the config column, the entire blob has to be updated
    if (tolerance !== current.tolerance || mapCenterLines !== current.mapCenterLines) {
      // Trigger mapping if tolerance change is above threshold to avoid no-diff changes
      isDirty ||= Math.abs(tolerance, current.tolerance) > 0.001;
      // Trigger mapping if centerline config is changed and system contains centerlines
      isDirty ||= mapCenterLines !== current.mapCenterLines && CU.hasCenterlines(this.facility, filter);

      hasChange = true;

      changes[QC.Config] = { tolerance, mapCenterLines };
    }

    if (hasChange) {
      changes[QC.LmvDbId] = current.dbId;

      await defaultModel.updateElementsWithData([changes], DtConstants.ChangeDesc.UpdateSystem);
    }

    // In case the system is already dirty, and the current update doesn't trigger mapping
    current.isDirty ||= isDirty;
    current.name = name;
    current.filter = filter;
    current.tolerance = tolerance;
    current.mapCenterLines = mapCenterLines;

    if (isDirty) {
      // Wait for the success response so the UI doesn't block if trigger fails
      await this.map(id);
    }

    return current;
  }

  /** @param {string[]} dbIds */
  async deleteMultiple(dbIds) {
    const defaultModel = this.facility.getDefaultModel();
    if (!defaultModel || !dbIds?.length) {
      return;
    }

    // Create mutation to mark default model system elements as deleted
    await defaultModel.deleteElements(dbIds, DtConstants.ChangeDesc.DeleteSystem);
  }

  async map(id) {
    const defaultModel = this.facility.getDefaultModel();
    if (!defaultModel) {
      return;
    }

    const elementKey = vlqID2ElementKey(id);
    const res = await fetchJson(
      defaultModel.loadContext,
      `/models/${defaultModel.urn()}/systems/${elementKey}/map`,
      'POST'
    );

    // Dispatch an immediate event to notify about mapping task
    this.facility.eventTarget.dispatchEvent({
      type: dte.DT_SYSTEMS_CHANGED_EVENT,
      change: {
        systemId: id,
        isPostProcessing: true
      }
    });

    return res;
  }

  /**
   * Retrieve connections for specified system
   * @param {string} id
   * @param {boolean} forceRefetch
   * @returns {Promise<System>} Stored system
   */
  async getConnections(id, forceRefetch = false) {

    const system = await this.get(id);

    if (system?.hasCachedConnections() && !forceRefetch) {
      // Already loaded, assuming it's being kept up-to-date
      return system;
    }

    // Cleanup previous connections and elements counter
    system.clear();

    // Determine which elements (by dbId) are part of the system
    const dbIdsByURN = CU.getFilteredDbIdsByURN(this.facility, system.filter);

    // Cache models to avoid getting from array each time when building connections
    // and create a promise of dbId -> connections/refs for each model
    const modelsData = {};
    for (const urn in dbIdsByURN) {
      const model = this.facility.getModelByUrn(urn);
      await model.waitForLoad(false, true);
      modelsData[urn] = {
        model,
        data: model.getDbIdToConns(dbIdsByURN[urn], system.id)
      };
    }

    // Build stored system
    for (const urn in dbIdsByURN) {
      // Wait for element model promise
      const { model, data } = modelsData[urn];
      const dbId2xConns = (await data)[0];

      for (const dbId of dbIdsByURN[urn]) {
        let element = system.getElement(urn, dbId);
        if (!element) {
          // Create&Add element to the system
          element = new SystemElement(model, dbId);
          system.addElement(element);
        }

        // Iterate through and create all connections
        for (const [connUrn, connElementID] of dbId2xConns[dbId]) {
          if (!modelsData[connUrn]) {
            // Asking for connections of a model that was likely filtered out.
            continue;
          }
          // Wait for element neighbour's model promise
          const { model: connModel, data: connData } = modelsData[connUrn];
          const resolvedRefs = (await connData)[1];

          const connDbId = resolvedRefs[connElementID];

          if (!connDbId) {
            // Neighbour's model promise determined that element is not part of the system
            // Most likely because it's a connection to an element that was removed due to
            // a filtered property change, just ignore it.
            continue;
          }

          let connectedElement = system.getElement(connUrn, connDbId);
          if (!connectedElement) {
            // Create&Add element to the system
            connectedElement = new SystemElement(connModel, connDbId);
            system.addElement(connectedElement);
          }

          // Add connection with element in the current system
          element.connect(connectedElement, SystemConnectionType.Next);
          connectedElement.connect(element, SystemConnectionType.Previous);
        }
      }
    }

    await this.updateSubsystemElements(system);

    CU.countSystemElements(this.facility, system);

    return system;
  }

  async getSystemsOf(modelRef, dbId) {
    let model = typeof modelRef === 'string' ? this.facility.getModelByUrn(modelRef) : modelRef;
    if (!model?.getData()) return [];

    const systems = await this.getAll();
    return systems.filter((s) => CU.isSystemElement(s, model, dbId));
  }

  async _getModelAttributeData(model, attrHash) {
    // See if there's a FacetSystems we can use; very likely it will be the case
    // since system related actions require the MEP Systems facet to be loaded
    const facetDef = this.facility.facetsManager.getFacetDef(FacetTypes.mepSystems);
    const cachedAttrData = facetDef?.getCachedAttributesData(model);

    if (cachedAttrData && cachedAttrData[attrHash]) {
      return cachedAttrData;
    }

    // Log cache miss, consider waiting for facet recalculation if this happens often
    console.log(`Attribute data not found in FacetSystem for ${model.urn()}.`);

    // This may happen before between facet cache invalidation and recalc on property change,
    // so fallback to the model's custom facets
    const facets = await model.getCustomFacets([attrHash]);

    if (!facets) return;

    return { [attrHash]: facets[0] };
  }

  async updateSubsystemElements(system) {
    if (!system.subsystems) return;

    system.subsystems.forEach((s) => {s.elements.length = 0;});

    let currModel, modelAttrData;
    for (const element of system.allElements()) {
      if (currModel !== element.model) {
        currModel = element.model;
        modelAttrData = await this._getModelAttributeData(currModel, system.subsystemAttrHash);
      }

      const subsystem = system.getElementSubsystem(modelAttrData, element.dbId);
      if (subsystem) {
        subsystem.elements.push(element);
      }
    }
  }

  /**
   * Call after finalizing changes locally. Store user changes to a System's connections.
   * @param {string} id
   * @param {SystemElement[]} elements
   * @return {Promise<*>[]} Array of promises from DtModel.prototype.updateElementsWithData
   */
  async updateConnections(id, elements) {

    if (!elements?.length) {
      return;
    }

    const system = await this.get(id);

    if (elements.some((e) => !system.hasElement(e.model.urn(), e.dbId))) {
      // Bail if some elements don't belong to the system
      console.error(`Failed to store System "${system.id}": System/Elements mismatch`);
      return;
    }

    const dbIdsByURN = CU.getElementsDbIdsByURN(elements);
    const fullIds = await this.facility.encodeIds(dbIdsByURN, true);

    const mutations = {};
    const overrideQC = DtConstants.ColumnFamilies.Systems + ":!" + system.id;
    let tempBuffer = new Uint8Array(RowKeySize);

    for (const element of elements) {
      const urn = element.model.urn();
      let perModel = mutations[urn] = mutations[urn] ?? [];

      const changeData = { [DtConstants.QC.LmvDbId]: element.dbId };

      // Store as directed graph
      const connCount = element.next.length;
      if (tempBuffer.length < connCount * RowKeySize) {
        tempBuffer = new Uint8Array(connCount * RowKeySize);
      }

      // We update override columns for every element in the change, so no need to ensure diff between stored/loaded.
      let offset = 0;
      for (const conn of element.next) {
        const fullId = fullIds[conn.model.urn()][conn.dbId];
        base64DecToArr(fullId, tempBuffer, offset);
        offset += RowKeySize;
      }

      if (connCount === 0) {
        // Empty list
        changeData[overrideQC] = STANDALONE_ELEMENT_CONN_VAL;
      } else {
        changeData[overrideQC] = base64EncArr(tempBuffer, 0, offset);
      }

      perModel.push(changeData);
    }

    const changePromises = [];

    for (const urn in mutations) {
      const model = this.facility.getModelByUrn(urn);
      const promise = model.updateElementsWithData(mutations[urn], DtConstants.ChangeDesc.UpdateSystemConnections);

      changePromises.push(promise);
    }

    const result = await Promise.all(changePromises);

    this.facility.eventTarget.dispatchEvent({ type: dte.DT_SYSTEM_CONNECTIONS_CHANGED_EVENT, systemId: system.id });

    return result;
  }

  /**
   * Mark system as dirty to indicate remapping is needed
   * Note the only way to mark system as not dirty is by remapping, in the importer
   * @param {string[]} id SystemID
   */
  async setAsDirty(ids) {
    const defaultModel = this.facility.getDefaultModel();
    if (!defaultModel) {
      return;
    }

    if (!Array.isArray(ids)) {
      ids = [ids];
    }

    const systems = await this.getAll();
    const systemsToMark = systems.filter((s) => ids.includes(s.id) && !s.isDirty);

    const data = systemsToMark.map((system) => ({
      [QC.LmvDbId]: system.dbId,
      [QC.State]: DtConstants.SystemState.Dirty
    }));

    if (!data.length) return;

    await defaultModel.updateElementsWithData(data, DtConstants.ChangeDesc.UpdateSystemState);

    systemsToMark.forEach((s) => s.isDirty = true);

    return systemsToMark;
  }

  /**
   * Create/Patch/Delete subsystems according to the given request Subsystems:
   * @param {String} systemId Parent system's VLQ ID
   * @param {String} attrHash Hash of attribute used for filtering
   * @param {[*]} reqSubsystems Expect list of subsystems, which can be existing or new.
   * @returns
   */
  async patchSubsystems(systemId, attrHash, reqSubsystems) {
    const system = await this.get(systemId);
    if (!system) return;

    const patchChangeData = [];
    const deleteDbIds = [];

    if (system.subsystems?.size) {
      // Update existing subsystems
      for (const curr of system.subsystems.values()) {
        // Note we look for subsystem with matching dbId, not id.
        // Since user can change the propVal associated with the subsystem
        const req = reqSubsystems.find((s) => s.dbId === curr.dbId);

        if (req) {
          // Update existing subsystem, if needed
          let changeData;
          if (curr.id !== req.id || curr.attrHash !== attrHash) {
            changeData ??= { [QC.LmvDbId]: curr.dbId };
            changeData[QC.Settings] = { [attrHash]: req.id }; // JSON BLOB
          }
          if (curr.name !== req.name) {
            changeData ??= { [QC.LmvDbId]: curr.dbId };
            changeData[QC.Name] = req.name;
          }

          if (changeData) {
            patchChangeData.push(changeData);
          }
        } else {
          // Remove existing subsystem if not in the new list
          deleteDbIds.push(curr.dbId);
        }
      }
    }

    const systemRef = vlqID2ElementKey(system.id, true /* skip flags */);
    const newChangeData = [];

    for (const req of reqSubsystems) {
      if (!req.dbId) {
        // New subsystem, we need to create it
        const changeData = {
          [QC.Parent]: systemRef,
          [QC.Name]: req.name,
          [QC.CategoryId]: SYSTEM_CATEGORY,
          [QC.ElementFlags]: ElementFlags.System,
          [QC.Settings]: { [attrHash]: req.id }
        };
        newChangeData.push(changeData);
      }
    }

    const defaultModel = this.facility.getDefaultModel();
    const changeMessage = system.subsystems?.size ? DtConstants.ChangeDesc.UpdateSubsystems : DtConstants.ChangeDesc.CreateSubsystems;
    const correlationId = v4();

    // No need to notify, this will trigger onOwnMutateEvent causing a refetch
    // No need to update attribute hash, it will be updated by refetch

    // Operations are ordered so Attribute hash is less likely to clash between events
    // Could be guaranteed by using a single request, but will need a new worker operation that supports all three
    deleteDbIds.length && (await defaultModel.deleteElements(deleteDbIds, changeMessage, correlationId));
    patchChangeData.length && (await defaultModel.updateElementsWithData(patchChangeData, changeMessage, correlationId));
    newChangeData.length && (await defaultModel.createElementsWithData(newChangeData, changeMessage, correlationId));
  }
}